import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchPicker from "@/components/SearchPicker";
import api, { formatApiError } from "@/lib/api";
import { formatVariantLabel, sortVariants } from "@/lib/variants";
import { fetchAvailability, aggregateForProduct } from "@/lib/stockAvailability";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Loader2, Package, Barcode, Layers, Grid3x3, FileText, LayoutList, Search, X, Copy, Edit3, Sparkles, Factory, FileSpreadsheet, Tags, Download, Upload, ChevronDown, Bookmark } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { matchesAll } from "@/lib/trFold";
import { toast } from "sonner";
import RecipeSheet from "@/components/RecipeSheet";
import MakeToStockDialog from "@/components/MakeToStockDialog";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NumericInput from "@/components/NumericInput";
import BulkPricingDialog from "@/components/BulkPricingDialog";
import StockOptionDialog from "@/components/StockOptionDialog";
import BulkClassifyDialog from "@/components/BulkClassifyDialog";
import ProductCategoriesDialog from "@/components/ProductCategoriesDialog";
import { duplicateProduct as duplicateProductApi } from "@/lib/duplicateProduct";
import { canProdEdit } from "@/lib/orderPerms";
import CurrencyChip, { CURRENCY_SYMBOL } from "@/components/CurrencyChip";
import { useCurrencyConversion, handleCurrencyChange } from "@/lib/currencyConvert";

const VAT_OPTIONS = [0, 1, 8, 10, 18, 20];
const productEmpty = {
  name: "",
  description: "",
  category: "",
  base_cost: "",
  base_price: "",
  purchase_price: "",
  purchase_tax_rate: 10,
  purchase_currency: "TRY",
  purchase_discount_percent: 0,
  sale_price: "",
  sale_tax_rate: 10,
  sale_currency: "TRY",
  // Perakende Fiyatlandırma (FAZ 1) — boş = tenant varsayılanı
  retail_markup_percent: "",
  // FAZ B — Tedarik/Karşılama tipi
  //   manufactured = Üretilen (varsayılan)
  //   traded       = Hazır / Ticari Ürün
  //   outsourced   = Fason / Dış Üretim
  supply_type: "manufactured",
};

// FAZ B — Ürün tedarik tipi meta (label + rozet renkleri UI'da paylaşılır)
export const SUPPLY_TYPE_META = {
  manufactured: { label: "Üretilen", short: "ÜRT", color: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/30" },
  traded: { label: "Hazır / Ticari", short: "TCR", color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/30" },
  outsourced: { label: "Fason", short: "FSN", color: "text-purple-500", bg: "bg-purple-500/10 border-purple-500/30" },
  unknown: { label: "Belirlenmemiş", short: "?", color: "text-rose-500", bg: "bg-rose-500/10 border-rose-500/40 animate-pulse" },
};
const matrixEmpty = {
  attributes: [
    { key: "beden", values: "S,M,L,XL,XXL" },
    { key: "renk", values: "Lacivert,Siyah" },
  ],
  cost: "",
  sales_price: "",
  stock_qty: "",
  sku_prefix: "",
};

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// FAZ B M2+ — Stok gösterim helper'ı.
// null/undefined → "—" (stocks kaydı YOK, veri bilinmiyor)
// 0 → "0" slate rengi (kaydı var ama sıfır)
// >0 → emerald pozitif
// <0 → rose negatif (has_negative_variance — sıfıra clamp EDİLMEZ)
// Türkçe binlik ayırıcı, kesirli sayılar için 2 hane.
const fmtStock = (n) => {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  const withDecimals = abs % 1 !== 0;
  return v.toLocaleString("tr-TR", {
    minimumFractionDigits: withDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
};
const stockColorClass = (n) => {
  if (n === null || n === undefined) return "text-slate-600";
  const v = Number(n);
  if (!Number.isFinite(v)) return "text-slate-600";
  if (v < 0) return "text-rose-400";
  if (v === 0) return "text-slate-500";
  return "text-emerald-400";
};

// FAZ 2C — kompakt "R · Y · K" satırı; yalnız anlamlı veri varsa gösterilir
// (rezerve/yolda yoksa liste kalabalıklaşmaz). null availRows → hata: hiçbir
// şey gösterilmez (yanlış 0 basılmaz).
const availSubline = (availRows, pid) => {
  if (!availRows) return null;
  const a = aggregateForProduct(availRows, pid);
  if (!(a.reserved > 0 || a.incoming > 0)) return null;
  return { reserved: a.reserved, incoming: a.incoming, available: a.available };
};

export default function Products() {
  const [products, setProducts] = useState([]);
  // FAZ 2C — batch availability (tek istek; null = alınamadı)
  const [availRows, setAvailRows] = useState(null);
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [optionInit, setOptionInit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(productEmpty);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixForm, setMatrixForm] = useState(matrixEmpty);
  const [saving, setSaving] = useState(false);
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [categoryDefs, setCategoryDefs] = useState([]);
  const [variantSets, setVariantSets] = useState([]);
  // Phase A #5: view toggle, search, filter, bulk selection
  const [view, setView] = useState("list"); // her açılışta Liste; kart görünümü düğmeyle (oturum içi)
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [brandFilter, setBrandFilter] = useState("");
  const [stockSort, setStockSort] = useState(null); // null | "desc" | "asc"
  const [showCount, setShowCount] = useState(300);
  useEffect(() => { setShowCount(300); }, [search, categoryFilter, brandFilter, stockSort, sortBy]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingProductId, setEditingProductId] = useState(null);
  // Perakende Fiyatlandırma (FAZ 1) — tenant varsayılan oranı + manuel fiyat koruması
  const [defaultMarkup, setDefaultMarkup] = useState(50);
  const [saleManual, setSaleManual] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  // FAZ 1 — Stok İçin Üretim dialog
  const [mtsOpen, setMtsOpen] = useState(false);
  const [mtsInitialProduct, setMtsInitialProduct] = useState(null);
  const { can } = useAuth();
  const canAiCreate = can("products", "ai_create") && can("products", "add");

  // FAZ 5 M6: merkezi TCMB conversion hook — sadece dialog açıkken fetch
  const { rates: fxRates, convert: fxConvert } = useCurrencyConversion(prodOpen);
  const tryEq = (amount, currency) => {
    const c = (currency || "TRY").toUpperCase();
    if (c === "TRY") return null;
    const n = Number(amount);
    if (!n) return null;
    const conv = fxConvert(n, c, "TRY");
    return conv;
  };

  // Currency değişince amount da TCMB kuru ile otomatik çevirilir (M6)
  const onPurchaseCurChange = handleCurrencyChange({
    rates: fxRates, convert: fxConvert,
    currentAmount: prodForm.purchase_price, currentCurrency: prodForm.purchase_currency,
    onApply: ({ amount, currency }) => setProdForm((p) => ({ ...p, purchase_price: amount, purchase_currency: currency })),
  });
  const onSaleCurChange = handleCurrencyChange({
    rates: fxRates, convert: fxConvert,
    currentAmount: prodForm.sale_price, currentCurrency: prodForm.sale_currency,
    onApply: ({ amount, currency }) => setProdForm((p) => ({ ...p, sale_price: amount, sale_currency: currency })),
  });

  // Perakende Fiyatlandırma (FAZ 1) — önerilen fiyat: NET ALIŞ × (100 + oran) / 100; yuvarlama YOK.
  // Net alış = alış × (1 - alış iskonto / 100); iskonto 0/boş ise net = alış (geriye dönük).
  const markupRate = (prodForm.retail_markup_percent === "" || prodForm.retail_markup_percent === null || prodForm.retail_markup_percent === undefined)
    ? defaultMarkup
    : Number(prodForm.retail_markup_percent);
  const purchaseNum = Number(prodForm.purchase_price);
  const purchaseDiscountNum = Number(prodForm.purchase_discount_percent || 0);
  const netPurchaseNum = Number.isFinite(purchaseNum) && purchaseNum > 0
    ? purchaseNum * (100 - (Number.isFinite(purchaseDiscountNum) && purchaseDiscountNum > 0 ? purchaseDiscountNum : 0)) / 100
    : null;
  const priceCurrenciesMatch = (prodForm.purchase_currency || "TRY").toUpperCase() === (prodForm.sale_currency || "TRY").toUpperCase();
  const suggestedPrice = priceCurrenciesMatch && netPurchaseNum !== null && Number.isFinite(markupRate)
    ? (netPurchaseNum * (100 + markupRate)) / 100
    : null;
  // Öneri gösterimi — 2 haneye sığmayan değerler 4 haneye kadar gösterilir (yuvarlama yapılmaz).
  const fmtPrice = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    const r2 = Math.round(v * 100) / 100;
    const needsMore = Math.abs(v - r2) > 1e-9;
    return v.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: needsMore ? 4 : 2 });
  };

  // Manuel fiyat koruması: kullanıcı satış fiyatını elle değiştirmediyse öneri otomatik uygulanır.
  useEffect(() => {
    if (!prodOpen || saleManual || suggestedPrice === null) return;
    setProdForm((p) => (Number(p.sale_price) === suggestedPrice ? p : { ...p, sale_price: suggestedPrice }));
  }, [suggestedPrice, saleManual, prodOpen]);

  const load = useCallback(async () => {
    try {
      const q = (search || "").trim();
      const params = q ? { search: q, limit: 5000 } : { limit: 5000 };
      const [p, c, vs, ps] = await Promise.all([
        api.get("/products", { params }),
        api.get("/definitions/product_category").catch(() => ({ data: [] })),
        api.get("/variant-sets").catch(() => ({ data: [] })),
        api.get("/products/pricing-settings").catch(() => ({ data: null })),
      ]);
      setProducts(p.data);
      // FAZ 2C — tek batch availability isteği (N+1 YOK); hata → null (satır gizlenir)
      const ids = (p.data || []).map((x) => x.id).filter(Boolean);
      if (ids.length) {
        fetchAvailability({ productIds: ids }).then(setAvailRows).catch(() => setAvailRows(null));
      } else {
        setAvailRows([]);
      }
      setCategoryDefs((c.data || []).filter((d) => d.is_active !== false));
      setVariantSets((vs.data || []).filter((s) => s.is_active !== false));
      if (ps.data) setDefaultMarkup(Number(ps.data.default_markup_percent ?? 50));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search]);

  // Debounced re-fetch on search input change
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  const loadVariants = async (p) => {
    setSelected(p);
    try {
      const { data } = await api.get(`/products/${p.id}/variants`);
      setVariants(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    // Initial load handled by debounced effect above (fires on mount).
    return undefined;
  }, []);

  // AI ile ürün oluşturulunca global dialog'dan gelen refresh event'ini dinle
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("fucan:products-refresh", handler);
    return () => window.removeEventListener("fucan:products-refresh", handler);
  }, []);

  const submitProduct = async (e) => {
    e.preventDefault();
    if (!prodForm.name.trim()) return;
    setSaving(true);
    try {
      const num = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const purchase = num(prodForm.purchase_price);
      const sale = num(prodForm.sale_price);
      const payload = {
        ...prodForm,
        base_cost: num(prodForm.base_cost) || purchase,
        base_price: num(prodForm.base_price) || sale,
        purchase_price: purchase,
        purchase_tax_rate: num(prodForm.purchase_tax_rate),
        purchase_currency: (prodForm.purchase_currency || "TRY").toUpperCase(),
        purchase_discount_percent: num(prodForm.purchase_discount_percent) || 0,
        sale_price: sale,
        sale_tax_rate: num(prodForm.sale_tax_rate),
        sale_currency: (prodForm.sale_currency || "TRY").toUpperCase(),
        // Perakende Fiyatlandırma (FAZ 1) — boş bırakılırsa tenant varsayılanı (null) kaydedilir
        retail_markup_percent: (prodForm.retail_markup_percent === "" || prodForm.retail_markup_percent === null || prodForm.retail_markup_percent === undefined) ? null : num(prodForm.retail_markup_percent),
        // FAZ B — Tedarik tipi
        supply_type: (prodForm.supply_type || "manufactured").toLowerCase(),
      };
      if (editingProductId) {
        await api.patch(`/products/${editingProductId}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/products", payload);
        toast.success("Ürün eklendi");
      }
      setProdOpen(false);
      setProdForm(productEmpty);
      setEditingProductId(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const openEditProduct = (p) => {
    setEditingProductId(p.id);
    setProdForm({
      name: p.name || "",
      category: p.category || "",
      brand: p.brand || "",
      description: p.description || "",
      base_cost: p.base_cost ?? "",
      base_price: p.base_price ?? "",
      purchase_price: p.purchase_price ?? "",
      purchase_tax_rate: p.purchase_tax_rate ?? 10,
      purchase_currency: p.purchase_currency || "TRY",
      purchase_discount_percent: p.purchase_discount_percent ?? 0,
      sale_price: p.sale_price ?? "",
      sale_tax_rate: p.sale_tax_rate ?? 10,
      sale_currency: p.sale_currency || "TRY",
      retail_markup_percent: p.retail_markup_percent ?? "",
      // FAZ B — Tedarik tipi (DB'deki değeri koru; sadece hiç yoksa manufactured fallback)
      supply_type: p.supply_type || (p.source === "bizim_hesap" ? "unknown" : "manufactured"),
      currency: p.currency || "TRY",
      season: p.season || "",
      sku_prefix: p.sku_prefix || "",
      is_active: p.is_active !== false,
    });
    // Mevcut satış fiyatı varsa "manuel" say — öneri sessizce üzerine yazmasın (FAZ 1)
    setSaleManual(Number(p.sale_price) > 0);
    setProdOpen(true);
  };

  const openNewProduct = () => {
    setEditingProductId(null);
    setProdForm({ ...productEmpty, retail_markup_percent: defaultMarkup });
    setSaleManual(false);
    setProdOpen(true);
  };

  // Satın alma ekranındaki "+ Yeni Ürün" → /urunler?new=1 mevcut Yeni Ürün formunu açar
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("new") === "1" && can("products", "add")) openNewProduct();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitMatrix = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const attrs = {};
    for (const row of matrixForm.attributes) {
      const k = row.key.trim();
      const vs = row.values.split(",").map((s) => s.trim()).filter(Boolean);
      if (k && vs.length) attrs[k] = vs;
    }
    if (!Object.keys(attrs).length) {
      toast.error("En az bir öznitelik ekleyin");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/products/${selected.id}/variants/matrix`, {
        attributes: attrs,
        cost: Number(matrixForm.cost),
        sales_price: Number(matrixForm.sales_price),
        stock_qty: Number(matrixForm.stock_qty),
        sku_prefix: matrixForm.sku_prefix.trim(),
      });
      toast.success(`${data.created} varyant oluşturuldu`);
      setMatrixOpen(false);
      setMatrixForm(matrixEmpty);
      await loadVariants(selected);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`${p.name} ve ${p.variant_count} varyantı silinecek. Devam?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Ürün silindi");
      if (selected?.id === p.id) setSelected(null);
      load();
      window.dispatchEvent(new CustomEvent("fucan:products-refresh"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicateProduct = async (p) => {
    // Ortak util (lib/duplicateProduct.js) — reçete + varyantlar dahil tek canonical akış
    const created = await duplicateProductApi(p);
    if (created) load();
  };

  const deleteVariant = async (v) => {
    if (!window.confirm(`${v.sku} silinecek. Devam?`)) return;
    try {
      await api.delete(`/variants/${v.id}`);
      setVariants((prev) => prev.filter((x) => x.id !== v.id));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addAttrRow = () => setMatrixForm({ ...matrixForm, attributes: [...matrixForm.attributes, { key: "", values: "" }] });
  const rmAttrRow = (i) => setMatrixForm({ ...matrixForm, attributes: matrixForm.attributes.filter((_, x) => x !== i) });

  // Phase A #5: filter / sort / bulk
  const setViewPersist = (v) => setView(v);
  const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort();
  const brands = Array.from(new Set(products.map((p) => (p.brand || "").trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, "tr")).map((b) => ({ id: b, name: b }));
  const filtered = products
    .filter((p) => {
      if (search.trim().length >= 2) {
        if (!matchesAll(search, [p.name, p.code, p.description, p.category])) return false;
      }
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (brandFilter && (p.brand || "").trim() !== brandFilter) return false;
      return true;
    })
    .sort((a, b) => {
      if (stockSort) return stockSort === "desc" ? (b.stock_total || 0) - (a.stock_total || 0) : (a.stock_total || 0) - (b.stock_total || 0);
      if (sortBy === "price") return (b.base_price || 0) - (a.base_price || 0);
      if (sortBy === "variants") return (b.variant_count || 0) - (a.variant_count || 0);
      return (a.name || "").localeCompare(b.name || "", "tr");
    });
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((p) => p.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} ürün ve tüm varyantları silinecek. Devam?`)) return;
    try {
      await Promise.all([...selectedIds].map((id) => api.delete(`/products/${id}`)));
      toast.success(`${selectedIds.size} ürün silindi`);
      setSelectedIds(new Set());
      load();
      window.dispatchEvent(new CustomEvent("fucan:products-refresh"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Ürünler & Varyantlar"
        subtitle="Beden × Renk × Kumaş × Logo matrisi ile sınırsız varyant"
        breadcrumbs={["Modüller", "Ürünler"]}
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={() => navigate("/el-terminali")} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold" data-testid="products-hand-terminal-btn">
              <Barcode className="w-4 h-4 mr-2" /> El Terminali
            </Button>
            {canAiCreate && (
              <Button
                data-testid="ai-product-open-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("fucan:open-ai-product", { detail: { prompt: "" } }))}
                className="bg-gradient-to-br from-fuchsia-600 to-purple-700 hover:from-fuchsia-500 hover:to-purple-600 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
                title="AI ile ürün oluştur (products.ai_create + products.add yetkisi gerekiyor)"
              >
                <Sparkles className="w-4 h-4 mr-2" /> AI ile Oluştur
              </Button>
            )}
            {!canAiCreate && can("products", "add") && (
              <Button
                disabled
                className="bg-slate-800 text-slate-500 rounded-sm uppercase tracking-wider text-xs font-semibold cursor-not-allowed"
                title="Bu işlem için AI ile ürün oluşturma (products.ai_create) yetkisi gerekiyor."
                data-testid="ai-product-open-btn-disabled"
              >
                <Sparkles className="w-4 h-4 mr-2" /> AI ile Oluştur
              </Button>
            )}
            {can("products", "view") && (
              <Button
                data-testid="bulk-pricing-open-btn"
                onClick={() => setBulkOpen(true)}
                variant="outline"
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
                title="Excel ile toplu fiyat görüntüleme / güncelleme"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Toplu Fiyat Yönetimi
              </Button>
            )}
            {can("products", "view") && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="bulk-product-menu-btn"
                    variant="outline"
                    className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
                    title="Excel ile toplu ürün işlemleri"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Toplu Ürün Yönetimi
                    <ChevronDown className="w-3.5 h-3.5 ml-1.5 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  data-testid="bulk-product-menu"
                  className="bg-slate-900 border-slate-800 text-slate-200 rounded-sm min-w-[220px]"
                >
                  <DropdownMenuItem
                    data-testid="menu-excel-export"
                    onClick={() => setClassifyOpen(true)}
                    className="text-xs cursor-pointer focus:bg-slate-800 focus:text-slate-100"
                  >
                    <Download className="w-3.5 h-3.5 mr-2 text-slate-400" /> Excel'e Aktar
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-excel-import"
                    onClick={() => setClassifyOpen(true)}
                    className="text-xs cursor-pointer focus:bg-slate-800 focus:text-slate-100"
                  >
                    <Upload className="w-3.5 h-3.5 mr-2 text-slate-400" /> Toplu Ürün Güncelle
                  </DropdownMenuItem>
                  {/* İleride: Yeni Ürün İçe Aktar, Stok Sayımı / Güncelle */}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {can("products", "view") && (
              <Button
                data-testid="categories-open-btn"
                onClick={() => setCatsOpen(true)}
                variant="outline"
                className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
                title="Dinamik ürün kategorilerini yönet (ekle / yeniden adlandır / aktif-pasif)"
              >
                <Tags className="w-4 h-4 mr-2" /> Kategoriler
              </Button>
            )}
            {can("products", "add") && (
              <Button
                data-testid="add-product-btn"
                onClick={openNewProduct}
                className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" /> Yeni Ürün
              </Button>
            )}
            {canProdEdit(can) && (
              <Button
                data-testid="mts-open-btn"
                onClick={() => {
                  // Ürün listesinde tek bir ürün seçili/aktifse pre-fill et
                  const active = selected || (selectedIds.size === 1 ? products.find((p) => selectedIds.has(p.id)) : null);
                  setMtsInitialProduct(active || null);
                  setMtsOpen(true);
                }}
                className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
                title="Müşteri/sipariş olmadan depoya stok üretmek için üretim emri oluştur"
              >
                <Factory className="w-4 h-4 mr-2" /> Stok İçin Üretim
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6 lg:p-8">
        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="products-toolbar">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="products-search-input"
              placeholder="Ara — ör: lac mont, polo t..."
              className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm h-8 pl-8 text-xs" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-2 text-slate-500 hover:text-slate-200">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="products-category-filter"
            className="bg-slate-900 border border-slate-800 rounded-sm text-xs text-slate-200 h-8 px-2">
            <option value="">Tüm kategoriler</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="w-48">
            <SearchPicker
              testId="products-brand-filter"
              items={brands}
              value={brandFilter}
              onChange={(b) => setBrandFilter(b || "")}
              getLabel={(b) => b.name}
              placeholder="Marka: Tümü"
              minChars={0}
            />
          </div>
          <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setStockSort(null); }}
            data-testid="products-sort"
            className="bg-slate-900 border border-slate-800 rounded-sm text-xs text-slate-200 h-8 px-2">
            <option value="name">Ada göre</option>
            <option value="price">Fiyata göre</option>
            <option value="variants">Varyant sayısına göre</option>
          </select>
          <div className="ml-auto flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button size="sm" onClick={bulkDelete} data-testid="bulk-delete-btn"
                className="bg-red-600 hover:bg-red-500 rounded-sm h-8 text-xs">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> {selectedIds.size} sil
              </Button>
            )}
            <div className="flex bg-slate-900 border border-slate-800 rounded-sm p-0.5">
              <button data-testid="view-grid-btn" onClick={() => setViewPersist("grid")}
                title="Kart Görünümü"
                className={`h-7 w-7 rounded-sm flex items-center justify-center ${view === "grid" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100"}`}>
                <Grid3x3 className="w-3.5 h-3.5" />
              </button>
              <button data-testid="view-list-btn" onClick={() => setViewPersist("list")}
                title="Liste Görünümü"
                className={`h-7 w-7 rounded-sm flex items-center justify-center ${view === "list" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100"}`}>
                <LayoutList className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-slate-900 border-slate-800 rounded-sm p-12 text-center">
            <Package className="w-10 h-10 mx-auto text-slate-700 mb-3" />
            <div className="text-slate-400 text-sm">
              {products.length === 0 ? "Henüz ürün yok. İlkini ekleyerek başlayın." : "Arama kriterlerinize uyan ürün bulunamadı."}
            </div>
          </Card>
        ) : view === "list" ? (
          <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden" data-testid="products-list-view">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="w-10">
                    <Checkbox checked={allVisibleSelected} onCheckedChange={toggleAll}
                      data-testid="select-all-products"
                      className="border-slate-600 data-[state=checked]:bg-blue-600" />
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kategori</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Varyant</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">
                    <button type="button" data-testid="products-stock-sort"
                      onClick={() => setStockSort((s) => (s === "desc" ? "asc" : "desc"))}
                      className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-slate-200 transition-colors">
                      Stok {stockSort === "desc" ? "↓" : stockSort === "asc" ? "↑" : "↕"}
                    </button>
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Maliyet</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Fiyat</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice(0, showCount).map((p) => (
                  <TableRow key={p.id} className="border-slate-800 cursor-pointer" data-testid={`product-row-${p.id}`}
                    onClick={() => navigate(`/urun/${p.id}`)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.has(p.id)} onCheckedChange={() => toggleOne(p.id)}
                        data-testid={`select-product-${p.id}`}
                        className="border-slate-600 data-[state=checked]:bg-blue-600" />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-100 flex items-center gap-2">
                        {p.name}
                        {(() => {
                          const st = SUPPLY_TYPE_META[p.supply_type || "manufactured"] || SUPPLY_TYPE_META.unknown;
                          return (
                            <span
                              data-testid={`product-supply-badge-${p.id}`}
                              className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-wider border ${st.bg} ${st.color}`}
                              title={`Tedarik: ${st.label}`}
                            >
                              {st.short}
                            </span>
                          );
                        })()}
                      </div>
                      {p.description && <div className="text-xs text-slate-500 max-w-[280px] truncate">{p.description}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-400">{p.category || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-blue-400">{p.variant_count}</TableCell>
                    <TableCell
                      className={`text-right font-mono text-xs ${stockColorClass(p.stock_total)}`}
                      data-testid={`product-stock-${p.id}`}
                      title={
                        p.source === "bizim_hesap" && p.bh_stock_available
                          ? "Bizim Hesap stok bakiyesi ürün ailesi seviyesindedir. Beden/varyant dağılımı kaynak API tarafından sağlanmıyor."
                          : undefined
                      }
                    >
                      {fmtStock(p.stock_total)}
                      {(() => {
                        const a = availSubline(availRows, p.id);
                        return a ? (
                          <div className="text-[9px] font-mono text-slate-500 mt-0.5" data-testid={`product-stock-avail-${p.id}`}>
                            R: <span className="text-amber-500">{fmtStock(a.reserved)}</span> · Y: <span className="text-sky-400">{fmtStock(a.incoming)}</span> · K: <span className="text-emerald-400">{fmtStock(a.available)}</span>
                          </div>
                        ) : null;
                      })()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-400">{CURRENCY_SYMBOL[p.purchase_currency] || "₺"}{fmt(p.purchase_price ?? p.base_cost)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-slate-100">{CURRENCY_SYMBOL[p.sale_currency] || "₺"}{fmt(p.sale_price ?? p.base_price)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openEditProduct(p)}
                        data-testid={`list-edit-${p.id}`}
                        title="Düzenle"
                        className="text-slate-400 hover:text-emerald-300 h-7 w-7 inline-flex items-center justify-center">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setOptionInit({ product: p, variantId: "" })}
                        data-testid={`list-option-${p.id}`}
                        title="Opsiyona Al"
                        className="text-slate-400 hover:text-sky-300 h-7 w-7 inline-flex items-center justify-center">
                        <Bookmark className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setRecipeProduct(p)}
                        data-testid={`list-recipe-${p.id}`}
                        title="Reçete"
                        className="text-slate-400 hover:text-blue-300 h-7 w-7 inline-flex items-center justify-center">
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => duplicateProduct(p)}
                        data-testid={`list-copy-${p.id}`}
                        title="Ürünü Kopyala (reçete + varyantlar dahil)"
                        className="text-slate-400 hover:text-emerald-300 h-7 w-7 inline-flex items-center justify-center">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteProduct(p)}
                        data-testid={`list-delete-${p.id}`}
                        title="Sil"
                        className="text-slate-500 hover:text-red-400 h-7 w-7 inline-flex items-center justify-center">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid-view">
            {filtered.slice(0, showCount).map((p) => (
              <Card
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="bg-slate-900 border-slate-800 rounded-sm p-5 hover:border-blue-600 transition-colors cursor-pointer group"
                onClick={() => navigate(`/urun/${p.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-600 flex items-center justify-center rounded-sm">
                    <Package className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      data-testid={`card-option-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); setOptionInit({ product: p, variantId: "" }); }}
                      className="text-slate-600 hover:text-sky-400 p-1"
                      title="Opsiyona Al"
                    >
                      <Bookmark className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`edit-product-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); openEditProduct(p); }}
                      className="text-slate-600 hover:text-emerald-400 p-1"
                      title="Ürünü Düzenle"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`copy-product-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); duplicateProduct(p); }}
                      className="text-slate-600 hover:text-emerald-400 p-1"
                      title="Ürünü Kopyala"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-product-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteProduct(p); }}
                      className="text-slate-600 hover:text-red-400 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="text-base font-bold text-slate-50 tracking-tight leading-tight">{p.name}</div>
                {p.category && (
                  <Badge className="mt-2 bg-slate-800 text-slate-300 border border-slate-700 rounded-sm text-[10px] uppercase tracking-wider">
                    {p.category}
                  </Badge>
                )}
                {p.description && (
                  <div className="text-xs text-slate-400 mt-2 line-clamp-2">{p.description}</div>
                )}
                <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1 text-blue-500 font-mono">
                    <Layers className="w-3 h-3" /> {p.variant_count} varyant
                  </div>
                  <div className="font-mono text-slate-300">
                    {CURRENCY_SYMBOL[p.sale_currency] || "₺"}{fmt(p.sale_price ?? p.base_price)} <span className="text-slate-500 text-[10px]">/ {CURRENCY_SYMBOL[p.purchase_currency] || "₺"}{fmt(p.purchase_price ?? p.base_cost)}</span>
                  </div>
                </div>
                {/* FAZ B M2+ — Toplam stok göstergesi (family + variant birleşik) */}
                <div
                  className="mt-2 flex items-center justify-between text-[11px] font-mono"
                  data-testid={`product-stock-card-${p.id}`}
                  title={
                    p.source === "bizim_hesap" && p.bh_stock_available
                      ? "Bizim Hesap stok bakiyesi ürün ailesi seviyesindedir. Beden/varyant dağılımı kaynak API tarafından sağlanmıyor."
                      : undefined
                  }
                >
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">Toplam Stok</span>
                  <span className={stockColorClass(p.stock_total)}>{fmtStock(p.stock_total)}</span>
                </div>
                {(() => {
                  const a = availSubline(availRows, p.id);
                  return a ? (
                    <div className="mt-1 text-[9px] font-mono text-slate-500" data-testid={`product-stock-avail-card-${p.id}`}>
                      R: <span className="text-amber-500">{fmtStock(a.reserved)}</span> · Y: <span className="text-sky-400">{fmtStock(a.incoming)}</span> · K: <span className="text-emerald-400">{fmtStock(a.available)}</span>
                    </div>
                  ) : null;
                })()}
                <button
                  data-testid={`open-recipe-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); setRecipeProduct(p); }}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-sm border border-slate-800 hover:border-blue-600 bg-slate-950/50 hover:bg-blue-950/20 text-xs uppercase tracking-wider text-slate-300 hover:text-blue-300 font-mono transition-colors"
                >
                  <FileText className="w-3.5 h-3.5" /> Reçete & Maliyet
                </button>
              </Card>
            ))}
          </div>
        )}
        {filtered.length > showCount && (
          <div className="flex items-center justify-center gap-3 py-4 text-xs text-slate-500 font-mono">
            {showCount} / {filtered.length} ürün gösteriliyor
            <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" data-testid="products-show-more"
              onClick={() => setShowCount((c) => c + 300)}>Daha fazla göster</Button>
          </div>
        )}

      </div>

      {/* Add / Edit product dialog */}
      <BulkPricingDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        defaultMarkup={defaultMarkup}
        canEdit={can("products", "edit")}
        onDone={load}
      />
      <BulkClassifyDialog
        open={classifyOpen}
        onOpenChange={setClassifyOpen}
        onDone={load}
      />
      <ProductCategoriesDialog
        open={catsOpen}
        onOpenChange={setCatsOpen}
        canEdit={can("products", "edit")}
      />
      <Dialog open={prodOpen} onOpenChange={(v) => { setProdOpen(v); if (!v) { setEditingProductId(null); setProdForm(productEmpty); setSaleManual(false); } }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg max-h-[92vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="text-slate-50 tracking-tight">
              {editingProductId ? "Ürün Düzenle" : "Yeni Ürün"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitProduct} className="flex flex-col flex-1 min-h-0">
            <div className="space-y-4 overflow-y-auto flex-1 px-6 py-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Ürün Adı *</Label>
              <Input data-testid="product-name-input" required value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kategori</Label>
                <Select value={prodForm.category || "__none"} onValueChange={(v) => setProdForm({ ...prodForm, category: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="product-category-select" className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500">
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-h-72">
                    <SelectItem value="__none">— Kategorisiz —</SelectItem>
                    {categoryDefs.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    {categoryDefs.length === 0 && (
                      <div className="px-2 py-3 text-center text-xs text-slate-500">
                        Kategori tanımlı değil. Ayarlar → Tanımlar sekmesinden ekleyin.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Marka / Season</Label>
                <Input value={prodForm.brand || ""} onChange={(e) => setProdForm({ ...prodForm, brand: e.target.value })} placeholder="Opsiyonel" className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500" />
              </div>
            </div>

            {/* FAZ B — Tedarik / Karşılama Tipi */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Tedarik Tipi *</Label>
              <Select
                value={prodForm.supply_type || "manufactured"}
                onValueChange={(v) => setProdForm({ ...prodForm, supply_type: v })}
              >
                <SelectTrigger
                  data-testid="product-supply-type-select"
                  className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                  <SelectItem value="unknown" data-testid="supply-type-unknown">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-rose-500" />
                      <span>Belirlenmemiş</span>
                      <span className="text-[10px] text-slate-500">— Bizim Hesap import varsayılanı</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="manufactured" data-testid="supply-type-manufactured">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span>Üretilen Ürün</span>
                      <span className="text-[10px] text-slate-500">— Ürün reçetesi → üretim akışı</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="traded" data-testid="supply-type-traded">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      <span>Hazır / Ticari Ürün</span>
                      <span className="text-[10px] text-slate-500">— stok kontrolü → eksik satın alma</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="outsourced" data-testid="supply-type-outsourced">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      <span>Fason / Dış Üretim</span>
                      <span className="text-[10px] text-slate-500">— sonraki faz</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-slate-500">
                {prodForm.supply_type === "unknown" && "⚠ Bu ürün henüz belirlenmemiş — sipariş satırlarında beklemede kalır. Üretime veya satın almaya YÖNLENDİRİLMEZ."}
                {prodForm.supply_type === "traded" && "Sipariş oluşturulduğunda önce stok rezerve edilir, eksik miktar satın alma ihtiyacına gönderilir. Üretim emri oluşturulmaz."}
                {prodForm.supply_type === "manufactured" && "Sipariş oluşturulduğunda ürün reçetesi üzerinden hammadde ihtiyacı ve üretim akışı tetiklenir."}
                {prodForm.supply_type === "outsourced" && "Fason iş akışı sonraki fazda; şimdilik üretilen ürün gibi davranır."}
              </div>
            </div>

            {/* Alış / Satış Fiyat + Para Birimi + KDV — FAZ 5 M5 */}
            <div className="rounded-sm border border-slate-800 bg-slate-950/40 p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fiyatlandırma</div>
              <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-end">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                    Alış Fiyatı ({CURRENCY_SYMBOL[prodForm.purchase_currency] || "₺"})
                  </Label>
                  <NumericInput
                    data-testid="product-purchase-price-input"
                    step="0.01"
                    min={0}
                    value={prodForm.purchase_price}
                    onChange={(v) => setProdForm({ ...prodForm, purchase_price: v })}
                    placeholder="0,00"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Alış Kur</Label>
                  <CurrencyChip
                    testId="product-purchase-currency"
                    value={prodForm.purchase_currency}
                    onChange={onPurchaseCurChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Alış KDV %</Label>
                  <Select
                    value={String(prodForm.purchase_tax_rate ?? 10)}
                    onValueChange={(v) => setProdForm({ ...prodForm, purchase_tax_rate: Number(v) })}
                  >
                    <SelectTrigger data-testid="product-purchase-tax-select" className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono w-[90px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      {VAT_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>%{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {tryEq(prodForm.purchase_price, prodForm.purchase_currency) != null && (
                <div
                  data-testid="product-purchase-try-equivalent"
                  className="text-[11px] font-mono text-emerald-400/80 -mt-1"
                >
                  ≈ ₺{fmt(tryEq(prodForm.purchase_price, prodForm.purchase_currency))} (TCMB canlı kur)
                </div>
              )}
              {/* Alış İskonto Oranı — net alış hesabı için */}
              <div className="grid grid-cols-2 gap-3 items-end border-t border-slate-800/60 pt-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Alış İskonto Oranı %</Label>
                  <NumericInput
                    data-testid="product-purchase-discount-input"
                    step="0.1" min={0} max={100}
                    value={prodForm.purchase_discount_percent}
                    onChange={(v) => setProdForm({ ...prodForm, purchase_discount_percent: v })}
                    placeholder="0"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Net Alış Fiyatı</Label>
                  <div
                    data-testid="product-net-purchase"
                    className="px-3 py-2 flex items-center rounded-sm border border-slate-800 bg-slate-900/60 font-mono text-sm text-amber-300"
                  >
                    {netPurchaseNum !== null && Number(prodForm.purchase_discount_percent) > 0
                      ? `${CURRENCY_SYMBOL[prodForm.purchase_currency] || "₺"}${fmtPrice(netPurchaseNum)}`
                      : "—"}
                  </div>
                </div>
              </div>
              {/* Perakende Fiyatlandırma Motoru — FAZ 1 */}
              <div className="grid grid-cols-2 gap-3 items-end border-t border-slate-800/60 pt-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Perakende Artış Oranı %</Label>
                  <NumericInput
                    data-testid="product-markup-rate-input"
                    step="0.1"
                    min={0}
                    value={prodForm.retail_markup_percent}
                    onChange={(v) => setProdForm({ ...prodForm, retail_markup_percent: v })}
                    placeholder={String(defaultMarkup)}
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Önerilen Perakende Satış Fiyatı</Label>
                  <div
                    data-testid="product-suggested-price"
                    className="px-3 py-2 flex items-center rounded-sm border border-slate-800 bg-slate-900/60 font-mono text-sm text-cyan-300"
                  >
                    {suggestedPrice !== null
                      ? `${CURRENCY_SYMBOL[prodForm.sale_currency] || "₺"}${fmtPrice(suggestedPrice)}`
                      : "—"}
                  </div>
                </div>
              </div>
              {!priceCurrenciesMatch && Number(prodForm.purchase_price) > 0 && (
                <div data-testid="product-currency-mismatch-warning" className="text-[11px] font-mono text-amber-400 -mt-1">
                  ⚠ Alış ({prodForm.purchase_currency}) ve satış ({prodForm.sale_currency}) para birimleri farklı — otomatik perakende önerisi hesaplanmadı.
                </div>
              )}
              {suggestedPrice !== null && Number(prodForm.sale_price) !== suggestedPrice && (
                <div className="flex items-center justify-between gap-3 rounded-sm border border-cyan-500/30 bg-cyan-500/5 px-3 py-2">
                  <span className="text-[11px] font-mono text-cyan-300" data-testid="product-suggested-notice">
                    Yeni önerilen fiyat: {CURRENCY_SYMBOL[prodForm.sale_currency] || "₺"}{fmtPrice(suggestedPrice)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    data-testid="apply-suggested-price-btn"
                    onClick={() => setProdForm({ ...prodForm, sale_price: suggestedPrice })}
                    className="bg-cyan-600 hover:bg-cyan-500 rounded-sm uppercase tracking-wider text-[10px] font-semibold h-7"
                  >
                    Önerilen Fiyatı Uygula
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-[1fr_auto_auto] gap-3 items-end">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                    Satış Fiyatı ({CURRENCY_SYMBOL[prodForm.sale_currency] || "₺"})
                  </Label>
                  <NumericInput
                    data-testid="product-sale-price-input"
                    step="0.01"
                    min={0}
                    value={prodForm.sale_price}
                    onChange={(v) => { setSaleManual(true); setProdForm({ ...prodForm, sale_price: v }); }}
                    placeholder="0,00"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Satış Kur</Label>
                  <CurrencyChip
                    testId="product-sale-currency"
                    value={prodForm.sale_currency}
                    onChange={onSaleCurChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Satış KDV %</Label>
                  <Select
                    value={String(prodForm.sale_tax_rate ?? 10)}
                    onValueChange={(v) => setProdForm({ ...prodForm, sale_tax_rate: Number(v) })}
                  >
                    <SelectTrigger data-testid="product-sale-tax-select" className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono w-[90px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                      {VAT_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>%{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {tryEq(prodForm.sale_price, prodForm.sale_currency) != null && (
                <div
                  data-testid="product-sale-try-equivalent"
                  className="text-[11px] font-mono text-emerald-400/80 -mt-1"
                >
                  ≈ ₺{fmt(tryEq(prodForm.sale_price, prodForm.sale_currency))} (TCMB canlı kur)
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Açıklama</Label>
              <Textarea rows={2} value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 resize-none" />
            </div>
            </div>
            <DialogFooter className="gap-2 px-6 py-4 border-t border-slate-800 shrink-0 bg-slate-950/60">
              <Button type="button" variant="outline" onClick={() => setProdOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button data-testid="save-product-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProductId ? "Güncelle" : "Kaydet")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Variants sheet */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="right" className="bg-slate-950 border-l border-slate-800 text-slate-100 w-full sm:max-w-3xl overflow-y-auto p-0">
          <SheetHeader className="px-6 py-5 border-b border-slate-800 bg-slate-900/60 sticky top-0 z-10 backdrop-blur">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün Varyantları</div>
            <SheetTitle className="text-slate-50 tracking-tight text-xl">{selected?.name}</SheetTitle>
            <div className="text-xs text-slate-400">{variants.length} varyant kayıtlı</div>
          </SheetHeader>
          <div className="p-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="add-matrix-btn" onClick={() => setMatrixOpen(true)} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                <Grid3x3 className="w-4 h-4 mr-2" /> Varyant Matrisi Oluştur
              </Button>
            </div>
            <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">SKU</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Barkod</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Renk · Beden</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alış</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Satış</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Stok</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-10 text-slate-500 text-sm">
                        Bu ürün için varyant yok. "Varyant Matrisi Oluştur" ile toplu oluşturun.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortVariants(variants).map((v) => (
                      <TableRow key={v.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`variant-row-${v.id}`}>
                        <TableCell className="font-mono text-xs text-blue-500 font-semibold">{v.sku}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">
                          <span className="inline-flex items-center gap-1"><Barcode className="w-3 h-3" />{v.barcode}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs font-semibold text-emerald-500 dark:text-emerald-400">
                            {formatVariantLabel(v.attributes, "—")}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300 text-xs">{fmt(v.cost)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-100 font-semibold text-xs">{fmt(v.sales_price)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums text-slate-300 text-xs">{v.stock_qty}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button data-testid={`option-variant-${v.id}`} variant="ghost" size="sm" title="Opsiyona Al"
                            onClick={() => setOptionInit({ product: selected, variantId: v.id })}
                            className="text-slate-400 hover:text-sky-300 hover:bg-sky-950/30 rounded-sm">
                            <Bookmark className="w-4 h-4" />
                          </Button>
                          <Button data-testid={`delete-variant-${v.id}`} variant="ghost" size="sm" onClick={() => deleteVariant(v)} className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        </SheetContent>
      </Sheet>

      <StockOptionDialog open={!!optionInit} onOpenChange={(o) => { if (!o) setOptionInit(null); }}
        initialProduct={optionInit?.product || null} initialVariantId={optionInit?.variantId || ""}
        onSuccess={() => load()} />

      {/* Matrix dialog */}
      <Dialog open={matrixOpen} onOpenChange={setMatrixOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight">Varyant Matrisi Oluştur</DialogTitle>
            <div className="text-xs text-slate-400 mt-1">
              Aşağıdaki setlerden tıklayarak seçim yap veya manuel öznitelik gir.
            </div>
          </DialogHeader>
          <form onSubmit={submitMatrix} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Varyant Setleri (Tanımlar → Ürünler → Varyantlar) */}
            <div className="space-y-3 border border-slate-800 bg-slate-950/40 rounded-sm p-3">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-slate-400 font-mono">
                  Setten Seç <span className="text-[10px] text-slate-500 normal-case">— tıkla, öznitelik olarak eklensin</span>
                </div>
                <a href="/varyantlar" className="text-[10px] uppercase tracking-widest font-mono text-blue-400 hover:text-blue-300">
                  Varyantlar sayfası →
                </a>
              </div>
              {variantSets.length === 0 ? (
                <div className="text-xs text-slate-500 py-2">
                  Set yok. Önce <a href="/varyantlar" className="text-blue-400 hover:underline">Varyantlar sayfasından</a> set oluşturun.
                </div>
              ) : (
                ["size", "color", "other"].map((type) => {
                  const inType = variantSets.filter((s) => s.variant_type === type);
                  if (inType.length === 0) return null;
                  const label = type === "size" ? "Beden Setleri" : type === "color" ? "Renk Setleri" : "Diğer";
                  return (
                    <div key={type} className="space-y-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {inType.map((s) => {
                          const isSelected = matrixForm.attributes.some(
                            (a) => a.key === s.variant_type && a.values === (s.options || []).join(",")
                          );
                          return (
                            <button
                              key={s.id}
                              type="button"
                              data-testid={`variant-set-pick-${s.id}`}
                              onClick={() => {
                                // Aynı isimde attribute varsa değeri güncelle, yoksa ekle
                                const key = s.variant_type;
                                const values = (s.options || []).join(",");
                                const idx = matrixForm.attributes.findIndex((a) => a.key === key);
                                const next = [...matrixForm.attributes];
                                if (idx >= 0) next[idx] = { ...next[idx], values };
                                else next.push({ key, values });
                                setMatrixForm({ ...matrixForm, attributes: next.filter((a) => a.key || a.values) });
                              }}
                              className={`inline-flex flex-col items-start gap-0.5 text-left px-2.5 py-1.5 border rounded-sm transition-colors ${
                                isSelected
                                  ? "border-blue-500 bg-blue-950/40 text-blue-200"
                                  : "border-slate-700 bg-slate-900 text-slate-300 hover:border-blue-700 hover:bg-blue-950/20"
                              }`}
                            >
                              <span className="text-[11px] font-semibold">{s.name}</span>
                              <span className="text-[10px] font-mono text-slate-500">
                                {(s.options || []).slice(0, 6).join(" · ")}
                                {s.options && s.options.length > 6 ? ` +${s.options.length - 6}` : ""}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">
                Manuel Öznitelikler <span className="text-[10px] text-slate-500 normal-case">— setten seçtiklerin buraya doldu, değerleri virgülle düzenleyebilirsin</span>
              </Label>
              {matrixForm.attributes.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={row.key}
                    onChange={(e) => {
                      const attrs = [...matrixForm.attributes];
                      attrs[i] = { ...attrs[i], key: e.target.value };
                      setMatrixForm({ ...matrixForm, attributes: attrs });
                    }}
                    placeholder="beden / renk"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 w-1/3 font-mono"
                  />
                  <Input
                    value={row.values}
                    onChange={(e) => {
                      const attrs = [...matrixForm.attributes];
                      attrs[i] = { ...attrs[i], values: e.target.value };
                      setMatrixForm({ ...matrixForm, attributes: attrs });
                    }}
                    placeholder="S,M,L,XL veya Lacivert,Siyah"
                    className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 flex-1 font-mono"
                  />
                  <Button type="button" onClick={() => rmAttrRow(i)} variant="outline" className="border-slate-700 bg-transparent text-red-400 hover:bg-red-950/30 rounded-sm shrink-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button type="button" onClick={addAttrRow} variant="outline" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm text-xs">
                <Plus className="w-3 h-3 mr-1" /> Boş Öznitelik Ekle
              </Button>
              {matrixForm.attributes.filter((a) => a.key && a.values).length > 0 && (
                <div className="text-[11px] text-slate-400 font-mono pt-1">
                  → Toplam <b className="text-blue-400">
                    {matrixForm.attributes
                      .filter((a) => a.key && a.values)
                      .reduce((acc, a) => acc * a.values.split(",").filter((v) => v.trim()).length, 1)}
                  </b> varyant üretilecek
                </div>
              )}
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2 col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">SKU Öneki</Label>
                <Input value={matrixForm.sku_prefix} onChange={(e) => setMatrixForm({ ...matrixForm, sku_prefix: e.target.value })} placeholder="Boş bırakılırsa ürün adından türetilir" className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Maliyet ₺</Label>
                <Input type="number" step="0.01" value={matrixForm.cost} onChange={(e) => setMatrixForm({ ...matrixForm, cost: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Satış ₺</Label>
                <Input type="number" step="0.01" value={matrixForm.sales_price} onChange={(e) => setMatrixForm({ ...matrixForm, sales_price: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Başlangıç Stok</Label>
                <Input type="number" value={matrixForm.stock_qty} onChange={(e) => setMatrixForm({ ...matrixForm, stock_qty: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm focus-visible:ring-blue-500 font-mono" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setMatrixOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
              <Button data-testid="save-matrix-btn" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Oluştur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <RecipeSheet
        product={recipeProduct}
        open={!!recipeProduct}
        onOpenChange={(v) => !v && setRecipeProduct(null)}
      />

      <MakeToStockDialog
        open={mtsOpen}
        onOpenChange={setMtsOpen}
        initialProduct={mtsInitialProduct}
      />
    </div>
  );
}
